import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Card,
  CardBody,
  Input,
  FormGroup,
  Row,
  Col,
} from "reactstrap";
import { axiosHelper, tokenHelper } from "../../../../stores/helpers";
import { toast } from "react-toastify";
import Select from "react-select";
import { useMenuStore } from "../../../../stores/Assets/menu";
import { ConciergeBell } from "lucide-react";
import { motion } from "framer-motion";
const SplashCustomizationModal = ({ isOpen, toggle, space, onUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [splashData, setSplashData] = useState(null);
  const [menus, setMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [serviceItems, setServiceItems] = useState([]);
  const [redirectToMenu, setRedirectToMenu] = useState(false);
  const [disableSplash, setDisableSplash] = useState(false);

  const { getMenus } = useMenuStore((state) => state);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    menu_button_caption: "",
    action__button_text: "",
    menu_url: "",
    service_action_type: null,
    is_active: true,
    action__button_service: {
      order_items: [
        {
          menu_item_id: null,
          quantity: 1,
          note: "",
          status: "PENDING",
          choices: [],
        },
      ],
      customer_name: "Anonymous",
      table_number: null,
    },
  });

  const fetchMenus = async () => {
    try {
      if (!space?.space_id) return;

      const token = await tokenHelper.getToken();

      // Fetch only menus linked to this space
      const menuList = await axiosHelper.get(
        `/menu/space/${space.space_id}/ordered`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // The response might be wrapped in a data property or be direct
      const fetchedMenus = menuList?.data?.menus || menuList?.menus || menuList || [];
      setMenus(fetchedMenus);
    } catch (error) {
      console.error("Error fetching menus:", error);
    }
  };

  // Fetch SERVICE items when a menu is selected
  useEffect(() => {
    const fetchServiceItems = async () => {
      if (selectedMenuId && menus.length > 0) {
        const selectedMenu = menus.find(m => m.menu_id === selectedMenuId);

        // First try to get items from loaded menu
        if (selectedMenu && selectedMenu.menu_items) {
          const serviceItems = selectedMenu.menu_items.filter(item => item.type === "SERVICE");
          setServiceItems(serviceItems);
        } else {
          // If menu_items not in response, fetch them
          try {
            const token = await tokenHelper.getToken();
            const menuItemsResponse = await axiosHelper.get(
              `/menu/${selectedMenuId}/items?perPage=999`,
              {
                headers: { Authorization: `Bearer ${token}` },
                suppressToast: true, // Suppress toast for 404 errors
              }
            );

            const allItems = menuItemsResponse?.items || [];
            const serviceItems = allItems.filter(item => item.type === "SERVICE");
            setServiceItems(serviceItems);
          } catch (error) {
            // Silently handle 404 (not found) - menu might not have items yet
            // This is expected and we show a message to the user
            if (error?.response?.status !== 404) {
              console.error("Error fetching menu items:", error);
            }
            setServiceItems([]);
          }
        }
      } else {
        setServiceItems([]);
      }
    };

    fetchServiceItems();
  }, [selectedMenuId, menus]);

  // Set selectedMenuId based on existing menu_item_id after menus are loaded
  useEffect(() => {
    const setMenuFromMenuItem = async () => {
      if (splashData && menus.length > 0 && !selectedMenuId) {
        const orderItem = splashData.action__button_service?.order_items?.[0];
        const menuItemId = orderItem?.menu_item_id;

        if (menuItemId) {
          // First try to find in loaded menus
          let foundMenuId = null;
          for (const menu of menus) {
            if (menu.menu_items?.some(item => item.menu_item_id === menuItemId)) {
              foundMenuId = menu.menu_id;
              break;
            }
          }

          // If not found in loaded menus, fetch the menu item to get its menu_id
          if (!foundMenuId) {
            try {
              const token = await tokenHelper.getToken();
              const menuItemResponse = await axiosHelper.get(
                `/menu-item/${menuItemId}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  suppressToast: true, // Suppress toast for 404 errors
                }
              );

              // Handle different response structures
              const menuId = menuItemResponse?.data?.menu_id || menuItemResponse?.menu_id;
              if (menuId) {
                foundMenuId = menuId;
              }
            } catch (error) {
              // Silently handle 404 (not found) - menu item might not exist anymore
              // This is expected when there's no menu item selected
              if (error?.response?.status !== 404) {
                console.error("Error fetching menu item:", error);
              }
            }
          }

          if (foundMenuId) {
            setSelectedMenuId(foundMenuId);
          }
        }
      }
    };

    setMenuFromMenuItem();
  }, [splashData, menus, selectedMenuId]);

  // Reset form data when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setFormData({
        title: "",
        subtitle: "",
        menu_button_caption: "",
        action__button_text: "",
        menu_url: "",
        is_active: true,
        action__button_service: {
          order_items: [
            {
              menu_item_id: null,
              quantity: 1,
              note: "",
              status: "PENDING",
              choices: [],
            },
          ],
          customer_name: "Anonymous",
          table_number: null,
        },
      });
      setSplashData(null);
      setRedirectToMenu(false);
      setSelectedMenuId(null);
      setServiceItems([]);
      setDisableSplash(false);
      setLoading(false);
      setSaving(false);
    }
  }, [isOpen]);

  // Reset form data when space changes (even if modal is open)
  useEffect(() => {
    if (isOpen && space?.space_id) {
      // Reset form data when space changes to prevent showing old data
      setFormData({
        title: "",
        subtitle: "",
        menu_button_caption: "",
        action__button_text: "",
        menu_url: "",
        is_active: true,
        action__button_service: {
          order_items: [
            {
              menu_item_id: null,
              quantity: 1,
              note: "",
              status: "PENDING",
              choices: [],
            },
          ],
          customer_name: "Anonymous",
          table_number: null,
        },
      });
      setSplashData(null);
      setRedirectToMenu(false);
      setSelectedMenuId(null);
      setServiceItems([]);
      setDisableSplash(false);
    }
  }, [space?.space_id]);

  // Fetch existing splash data
  useEffect(() => {
    const fetchSplash = async () => {
      if (!space?.space_id || !isOpen) return;

      try {
        setLoading(true);
        const token = await tokenHelper.getToken();
        const response = await axiosHelper.get(`/splash/space/${space.space_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response) {
          setSplashData(response);
          const hasMenuUrl = !!response.menu_url;
          setRedirectToMenu(hasMenuUrl);
          setDisableSplash(response.is_active === false);

          setFormData({
            title: response.title || "",
            subtitle: response.subtitle || "",
            menu_button_caption: response.menu_button_caption || "",
            action__button_text: response.action__button_text || "",
            service_action_type: response.service_action_type || null,
            menu_url: response.menu_url || "",
            is_active: response.is_active !== false,
            action__button_service: response.action__button_service || {
              order_items: [
                {
                  menu_item_id: null,
                  quantity: 1,
                  note: "",
                  status: "PENDING",
                  choices: [],
                },
              ],
              customer_name: "Anonymous",
              table_number: null,
            },
          });
        } else {
          // No splash found - reset to defaults
          setFormData({
            title: "",
            subtitle: "",
            menu_button_caption: "",
            action__button_text: "",
            menu_url: "",
            is_active: true,
            action__button_service: {
              order_items: [
                {
                  menu_item_id: null,
                  quantity: 1,
                  note: "",
                  status: "PENDING",
                  choices: [],
                },
              ],
              customer_name: "Anonymous",
              table_number: null,
            },
          });
          setRedirectToMenu(false);
          setDisableSplash(false);
        }
      } catch (error) {
        // If 404, splash doesn't exist yet - reset to defaults
        if (error?.response?.status === 404) {
          setFormData({
            title: "",
            subtitle: "",
            menu_button_caption: "",
            action__button_text: "",
            menu_url: "",
            is_active: true,
            action__button_service: {
              order_items: [
                {
                  menu_item_id: null,
                  quantity: 1,
                  note: "",
                  status: "PENDING",
                  choices: [],
                },
              ],
              customer_name: "Anonymous",
              table_number: null,
            },
          });
          setRedirectToMenu(false);
          setDisableSplash(false);
          setSplashData(null);
        } else {
          console.error("Error fetching splash:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && space?.space_id) {
      fetchSplash();
      fetchMenus();
    }
  }, [isOpen, space?.space_id]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleActionServiceChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      action__button_service: {
        ...prev.action__button_service,
        [field]: value,
      },
    }));
  };

  const updateOrderItem = (field, value) => {
    setFormData((prev) => {
      const currentOrderItem = prev.action__button_service?.order_items?.[0] || {
        menu_item_id: null,
        quantity: 1,
        note: "",
        status: "PENDING",
        choices: [],
      };
      return {
        ...prev,
        action__button_service: {
          ...(prev.action__button_service || {}),
          order_items: [
            {
              ...currentOrderItem,
              [field]: value,
            },
          ],
        },
      };
    });
  };


  const handleSave = async () => {
    try {
      setSaving(true);
      const token = await tokenHelper.getToken();

      // Validate action__button_service order_items
      const orderItem = formData.action__button_service?.order_items?.[0];
      const menuItemId = orderItem?.menu_item_id;

      // Set quantity to 1 by default
      const validOrderItems = menuItemId ? [{
        ...orderItem,
        menu_item_id: menuItemId,
        quantity: 1, // Always set to 1
      }] : [];

      const payload = {
        title: formData.title,
        subtitle: formData.subtitle,
        menu_button_caption: formData.menu_button_caption,
        action__button_text: formData.action__button_text,
        service_action_type: formData.service_action_type || null,
        menu_url: redirectToMenu ? formData.menu_url : null,
        is_active: !disableSplash, // Use disableSplash switch value
        menu_item_id: (!formData.service_action_type && menuItemId) ? menuItemId : null,
        action__button_service: (validOrderItems.length > 0 && !formData.service_action_type) ? {
          ...(formData.action__button_service || {}),
          order_items: validOrderItems,
        } : null,
      };

      const response = await axiosHelper.put(
        `/splash/space/${space.space_id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update space has_splash flag based on disableSplash
      await axiosHelper.patch(
        `/v2/space/${space.space_id}`,
        { has_splash: !disableSplash },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(t("Splash configuration saved successfully!"));
      onUpdate();
      toggle();
    } catch (error) {
      console.error("Error saving splash:", error);
      toast.error(
        error?.response?.data?.message || t("Failed to save splash configuration")
      );
    } finally {
      setSaving(false);
    }
  };

  // Preview component - matches MenuInitialPage layout
  const PreviewPanel = () => {
    const displaySiteName = space?.site_name || space?.site?.name || "Site Name";
    const displayTitle = formData.title || null;
    const displaySubtitle = formData.subtitle || "Choose and Order ... We will bring it to your table.";
    const buttonText = formData.menu_button_caption || "Check menu";
    const actionText = formData.action__button_text || "Call waiter";

    return (
      <Card className="h-100">
        <CardBody>
          <h6 className="mb-3">{t("Preview")}</h6>
          <div
            className="d-flex flex-column align-items-center justify-content-center p-4"
            style={{
              minHeight: "600px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            {/* Header Section - matches MenuInitialPage */}
            <div className="my-3 d-flex flex-column align-items-center gap-2">
              {/* Logo */}
              <img
                src={space?.menu_site_image || space?.site_image || space?.site?.image_url || "/logo.png"}
                alt="Logo"
                className="rounded-circle"
                style={{ width: "100px", height: "100px", objectFit: "cover" }}
              />

              {/* Site Name */}
              <h1 className="mb-2 text-center" style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#111827" }}>
                {displaySiteName}
              </h1>

              {/* Title (below site name) */}
              {displayTitle ? (
                <p className="text-center" style={{ fontSize: "1rem", color: "#4b5563" }}>
                  {displayTitle}
                </p>
              ) : (
                <p className="text-center" style={{ fontSize: "1rem", color: "#4b5563" }}>
                  {t("Space:")} {space?.name || "Space Name"}
                </p>
              )}

              {/* Divider */}
              <div style={{ height: "1px", width: "100%", maxWidth: "320px", backgroundColor: "#e5e7eb" }} />
            </div>

            {/* Main Content */}
            <div className="mb-5 w-100" style={{ maxWidth: "384px" }}>
              {/* Subtitle (description before buttons) */}
              {displaySubtitle && (
                <p className="text-center my-3" style={{ fontSize: "1rem", color: "#374151" }}>
                  {displaySubtitle}
                </p>
              )}

              {/* Buttons */}
              <div className="d-flex flex-column gap-4">
                {/* Check Menu Button */}
                <button
                  className="btn w-100 d-flex align-items-center justify-content-center gap-3"
                  style={{
                    height: "56px",
                    backgroundColor: "#c59253",
                    borderColor: "#c59253",
                    color: "white",
                    fontSize: "1.125rem",
                    fontWeight: "600",
                    borderRadius: "9999px",
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <svg
                    style={{ width: "24px", height: "24px" }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  {buttonText}
                </button>

                {/* Action Button */}
                <button
                  className="btn w-100 d-flex align-items-center justify-content-center gap-3"
                  style={{
                    height: "56px",
                    margin: "0 auto",
                    border: "2px solid #d1d5db",
                    backgroundColor: "#f8fafc",
                    color: "#374151",
                    fontSize: "1.125rem",
                    fontWeight: "600",
                    borderRadius: "9999px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <ConciergeBell className="h-5 w-5 mr-2" />

                  {actionText}
                </button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {t("Customize Splash Screen")} - {space?.name}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center p-5">
            <Spinner />
            <p className="mt-2">{t("Loading...")}</p>
          </div>
        ) : (
          <Row>
            <Col md={6}>
              <div className="d-flex flex-column gap-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {/* Disable Splash Switch - at the top */}
                {/* <Card>
                  <CardBody>
                    <FormGroup>
                      <div className="form-check form-switch">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          checked={disableSplash}
                          onChange={(e) => setDisableSplash(e.target.checked)}
                          id="disableSplash"
                        />
                        <Label className="form-check-label" for="disableSplash">
                          {t("Disable Splash")}
                        </Label>
                      </div>
                      <small className="text-muted d-block mt-1">
                        {t("This will only take effect when you save")}
                      </small>
                    </FormGroup>
                  </CardBody>
                </Card> */}

                {/* Basic Information */}
                <Card>
                  <CardBody>
                    <h6 className="mb-3">{t("Basic Information")}</h6>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{t("Title")}</Label>
                          <Input
                            value={formData.title}
                            onChange={(e) => handleInputChange("title", e.target.value)}
                            placeholder="Space: {Space Name}"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{t("Subtitle")}</Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={formData.subtitle}
                            onChange={(e) => handleInputChange("subtitle", e.target.value)}
                            placeholder="Choose and Order ... We will bring it to your table."
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Button Configuration */}
                <Card>
                  <CardBody>
                    <h6 className="mb-3">{t("Button Configuration")}</h6>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{t("Menu Button Caption")}</Label>
                          <Input
                            value={formData.menu_button_caption}
                            onChange={(e) => handleInputChange("menu_button_caption", e.target.value)}
                            placeholder="Check menu"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <div className="form-check form-switch">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          checked={redirectToMenu}
                          onChange={(e) => setRedirectToMenu(e.target.checked)}
                          id="redirectToMenu"
                        />
                        <Label className="form-check-label" for="redirectToMenu">
                          {t("Redirect to Menu URL")}
                        </Label>
                      </div>
                    </FormGroup>
                    {redirectToMenu && (
                      <FormGroup>
                        <Label>{t("Menu URL")}</Label>
                        <Input
                          value={formData.menu_url}
                          onChange={(e) => handleInputChange("menu_url", e.target.value)}
                          placeholder="https://example.com/menu"
                        />
                      </FormGroup>
                    )}
                  </CardBody>
                </Card>

                {/* Action Service Configuration */}
                <Card>
                  <CardBody>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{t("Action Type")}</Label>
                          <Select
                            options={[
                              { value: null, label: t("None (Standard View)") },
                              { value: "CALL_WAITER", label: t("Call Waiter (Predefined)") },
                              { value: "custom", label: t("Custom Service Item") },
                            ]}
                            value={
                              formData.service_action_type === "CALL_WAITER"
                                ? { value: "CALL_WAITER", label: t("Call Waiter (Predefined)") }
                                : formData.service_action_type === null && (formData.action__button_service?.order_items?.[0]?.menu_item_id || selectedMenuId)
                                  ? { value: "custom", label: t("Custom Service Item") }
                                  : { value: null, label: t("None (Standard View)") }
                            }
                            onChange={(option) => {
                              const val = option?.value;
                              if (val === "CALL_WAITER") {
                                handleInputChange("service_action_type", "CALL_WAITER");
                                handleInputChange("action__button_text", "Call waiter");
                                updateOrderItem("menu_item_id", null);
                              } else if (val === "custom") {
                                handleInputChange("service_action_type", null);
                                if (!formData.action__button_text) {
                                  handleInputChange("action__button_text", "Order");
                                }
                              } else {
                                handleInputChange("service_action_type", null);
                                updateOrderItem("menu_item_id", null);
                                handleInputChange("action__button_text", null);
                              }
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {(formData.service_action_type || formData.action__button_service?.order_items?.[0]?.menu_item_id || selectedMenuId) && (
                      <Row className="mt-2">
                        <Col md={12}>
                          <FormGroup>
                            <Label>{t("Action Button Caption")}</Label>
                            <Input
                              value={formData.action__button_text || ""}
                              onChange={(e) => handleInputChange("action__button_text", e.target.value)}
                              placeholder={formData.service_action_type === "CALL_WAITER" ? "Call waiter" : "Order"}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    {/* Menu Selector - only show if custom is selected or if there's no service_action_type but there IS a menu_item_id */}
                    {!(formData.service_action_type || (!formData.service_action_type && !formData.action__button_service?.order_items?.[0]?.menu_item_id && selectedMenuId === null)) && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        {menus.length === 0 ? (
                          <div className="alert alert-warning">
                            <p className="mb-2">{t("No menus found for this space. Please create a menu first.")}</p>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => {
                                toggle();
                                navigate("/menus");
                              }}
                            >
                              {t("Go to Menus")}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <FormGroup>
                              <Label>{t("Select Menu")}</Label>
                              <Select
                                options={menus.map((menu) => ({
                                  value: menu.menu_id,
                                  label: menu.name || `Menu ${menu.menu_id}`,
                                }))}
                                value={
                                  menus
                                    .filter((m) => m.menu_id === selectedMenuId)
                                    .map((m) => ({
                                      value: m.menu_id,
                                      label: m.name || `Menu ${m.menu_id}`,
                                    }))[0] || null
                                }
                                onChange={(option) => {
                                  setSelectedMenuId(option?.value || null);
                                  updateOrderItem("menu_item_id", null); // Reset menu item selection when menu changes
                                }}
                                placeholder={t("Select a menu")}
                              />
                            </FormGroup>

                            {selectedMenuId && (
                              <>
                                {serviceItems.length === 0 ? (
                                  <div className="alert alert-warning">
                                    <p className="mb-2">{t("No SERVICE items found in this menu. Please create a SERVICE item first.")}</p>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => {
                                        toggle();
                                        navigate(`/menu-items/${selectedMenuId}`);
                                      }}
                                    >
                                      {t("Go to Menu Items")}
                                    </Button>
                                  </div>
                                ) : (
                                  <FormGroup>
                                    <Label>{t("Select Menu Item")}</Label>
                                    <Select
                                      options={serviceItems.map((item) => ({
                                        value: item.menu_item_id,
                                        label: item.title || `Item ${item.menu_item_id}`,
                                      }))}
                                      value={
                                        serviceItems
                                          .filter((mi) => mi.menu_item_id === formData.action__button_service?.order_items?.[0]?.menu_item_id)
                                          .map((mi) => ({
                                            value: mi.menu_item_id,
                                            label: mi.title || `Item ${mi.menu_item_id}`,
                                          }))[0] || null
                                      }
                                      onChange={(option) =>
                                        updateOrderItem("menu_item_id", option?.value || null)
                                      }
                                      placeholder={t("Select menu item")}
                                    />
                                  </FormGroup>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </motion.div>
                    )}

                    <FormGroup>
                      <Label>{t("Note")}</Label>
                      <Input
                        type="textarea"
                        rows={2}
                        value={formData.action__button_service?.order_items?.[0]?.note || ""}
                        onChange={(e) =>
                          updateOrderItem("note", e.target.value)
                        }
                        placeholder={t("Optional note")}
                      />
                    </FormGroup>
                  </CardBody>
                </Card>
              </div>
            </Col>
            <Col md={6}>
              <PreviewPanel />
            </Col>
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          {t("Cancel")}
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("Saving...")}
            </>
          ) : (
            t("Save")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SplashCustomizationModal;
